import { Badge, Button, Col, Divider, Image, Row, Space, Tag } from 'antd';
import clsx from 'clsx';
import { map } from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { CloseIcon, MessageIcon } from '../../assets/svg';
import {
  BREAKPOINTS,
  DATE_TIME_WITH_AT,
  GA_EVENT,
  GA_LABEL,
  REGISTER_LOG_STATUS,
} from '../../common/constants';
import { Event } from '../../common/trackEvents';
import { titleCase } from '../../common/utils';
import CommonPopover from '../../components/CommonPopover';
import CommonTooltip from '../../components/CommonTooltip';
import EllipsisText from '../../components/EllipsisText';

function RegisterCommentPopUp({
  previousHistoryData,
  fieldData,
  data,
  onClick,
  hasComment,
  fieldType,
  rowIndex,
}) {
  const [open, setOpen] = useState(false);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [isVisible, setIsVisible] = useState(false);
  const [previewImageKey, setPreviewImageKey] = useState('');
  const [commentData, setCommentData] = useState([]);

  useEffect(() => {
    setCommentData(
      previousHistoryData?.[0]?.registerLogDetailEntries?.comments ||
        previousHistoryData?.[0]?.comments,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousHistoryData, fieldData?.id, fieldType, rowIndex]);

  const changeStageStatus = (status) => {
    switch (status) {
      case REGISTER_LOG_STATUS.APPROVED:
        return (
          <Tag className="active-tag">
            {titleCase(REGISTER_LOG_STATUS.APPROVED)}
          </Tag>
        );
      case REGISTER_LOG_STATUS.APPROVAL_PENDING:
        return (
          <Tag className="yellow-tag">
            {titleCase(REGISTER_LOG_STATUS.APPROVAL_PENDING)}
          </Tag>
        );
      case REGISTER_LOG_STATUS.REDO:
        return <Tag color="red">{titleCase(REGISTER_LOG_STATUS.REDO)}</Tag>;
      case REGISTER_LOG_STATUS.REJECTED:
        return <Tag color="red">{titleCase(REGISTER_LOG_STATUS.REJECTED)}</Tag>;
      default:
        return <Tag className="inactive-tag">{titleCase(status)}</Tag>;
    }
  };

  return (
    <CommonPopover
      classNames={{
        root: 'comment-popup',
        container: 'comment-popup-container',
      }}
      onOpenChange={(e) => {
        if (commentData?.length > 0) {
          setOpen(e);
        } else {
          onClick();
        }
      }}
      getPopupContainer={() =>
        // eslint-disable-next-line no-undef
        document.querySelector('.register-details')
      }
      title={
        <div className="d-flex justify-between align-center">
          <div>Previous Comments</div>
          <CloseIcon
            height={24}
            width={24}
            className="pointer"
            onClick={() => setOpen(false)}
          />
        </div>
      }
      trigger="click"
      placement={isDesktopViewport ? 'left' : 'top'}
      open={open}
      fresh
      content={
        <div>
          {data?.status === REGISTER_LOG_STATUS.APPROVAL_PENDING && (
            <>
              <div className="d-flex align-center mb-10">
                <div className="level-name mr-10">{data?.levelName}</div>
                {changeStageStatus(data?.status)}
              </div>
              <div className="add-comment-button">
                <Button
                  type="primary"
                  shape="round"
                  icon={<MessageIcon className="mr-5" />}
                  onClick={() => {
                    setOpen(false);
                    onClick();
                  }}
                  className={clsx(
                    'width-percent-100 d-flex justify-center align-center',
                    hasComment && 'general-comment',
                  )}
                >
                  {hasComment ? 'Edit Comment' : 'Add Comment'}
                </Button>
              </div>
              {commentData?.length > 0 && <Divider dashed />}
            </>
          )}
          <div>
            {map(commentData, (item, index) => {
              return (
                item?.commentText && (
                  <div key={index}>
                    <div>
                      <div className="d-flex align-center">
                        <div className="level-name mr-10">
                          {item?.approvalLevelName || 'Level'}
                        </div>
                        {changeStageStatus(item?.registerLogStatus)}
                      </div>
                      <div className="comment-data-card mt-10">
                        <div className="name-and-time d-flex">
                          <CommonTooltip title={item?.commenter?.name}>
                            <div className="ellipsis-text">
                              {item?.commenter?.name}
                            </div>
                          </CommonTooltip>
                          <Badge
                            status="default"
                            className="ml-5 mr-5"
                            color="#676C77"
                          />
                          {moment(item?.createdAt)
                            ?.tz(data?.register?.project?.timeZone)
                            ?.format(DATE_TIME_WITH_AT)}
                        </div>
                        <div className="comment mt-8">
                          <EllipsisText text={item?.commentText} />
                        </div>
                        {item?.assetUrls?.length > 0 && (
                          <Row
                            gutter={18}
                            className="mt-16"
                            align="middle"
                            justify="space-between"
                          >
                            <Col>
                              <Space
                                orientation="vertical"
                                size={12}
                                className="mt-12"
                              >
                                <div>
                                  <Image.PreviewGroup
                                    preview={{
                                      visible:
                                        item?.createdAt === previewImageKey &&
                                        isVisible,
                                      onVisibleChange: (visible) => {
                                        setIsVisible(visible);
                                        setOpen(!visible);
                                        if (visible) {
                                          Event(GA_EVENT.OPENED_IMAGE_VIEWER, {
                                            label: GA_LABEL.OPENED_IMAGE_VIEWER,
                                            // eslint-disable-next-line no-undef
                                            pathname: window?.location?.href,
                                          });
                                        }
                                      },
                                    }}
                                  >
                                    <Space className="image-preview-wrapper issue-images">
                                      {React.Children.map(
                                        item?.assetUrls,
                                        (photo) => (
                                          <Image
                                            src={photo}
                                            alt="logo"
                                            height="40px"
                                            width="40px"
                                            onClick={() =>
                                              setPreviewImageKey(
                                                item?.createdAt,
                                              )
                                            }
                                          />
                                        ),
                                      )}
                                      {item?.assetUrls?.length > 1 && (
                                        <div
                                          className="extra-count pointer"
                                          onClick={() => {
                                            setIsVisible(true);
                                            setOpen(false);
                                            setPreviewImageKey(item?.createdAt);
                                          }}
                                        >
                                          {`+${item?.assetUrls?.length - 1}`}
                                        </div>
                                      )}
                                    </Space>
                                  </Image.PreviewGroup>
                                </div>
                              </Space>
                            </Col>
                          </Row>
                        )}
                      </div>
                      {commentData?.length - 1 > index && <Divider dashed />}
                    </div>
                  </div>
                )
              );
            })}
          </div>
        </div>
      }
    >
      {(data?.status === REGISTER_LOG_STATUS.APPROVAL_PENDING ||
        commentData?.length > 0) && (
        <Button
          type="primary"
          shape="round"
          icon={<MessageIcon />}
          onClick={() => {
            if (commentData?.length > 0) {
              setOpen(true);
            } else {
              onClick();
            }
          }}
          className={clsx(
            hasComment && 'general-comment',
            !isDesktopViewport
              ? 'width-percent-100 justify-center mb-24'
              : 'mb-24',
          )}
        >
          <>
            {commentData?.length > 0 && (
              <>
                {commentData?.length}
                <Badge color="white" className="mr-5 ml-5" />
              </>
            )}
            {(() => {
              if (commentData?.length > 0) {
                return `Comment${commentData?.length > 1 ? 's' : ''}`;
              }
              if (
                data?.status === REGISTER_LOG_STATUS.APPROVAL_PENDING &&
                hasComment
              ) {
                return 'Edit Comment';
              }
              return 'Add Comment';
            })()}
          </>
        </Button>
      )}
    </CommonPopover>
  );
}

export default RegisterCommentPopUp;
